import { useEffect, useState } from "react";
import { Button, Map, Modal, Typography } from "@/components/atoms";
import { FormBottomSheet } from "@/components/molecules";
import { LocationAddressAutocomplete } from "@/components/organisms/MyFavourites/FavouriteLocationsTable/components/LocationAddressAutocomplete";
import {
  LatLng,
  poiFieldLabel,
  geocoderResultToPoi,
  isPoiInCountries,
} from "@/components/organisms/MyFavourites/FavouriteLocationsTable/suggestions";
import { useCurrentLocation } from "@/hooks/useCurrentLocation";
import { useIsMdUp } from "@/hooks/useIsMdUp";
import { snapToNearestRoad } from "@/lib/googleRoads";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setActiveFieldLocation, setIsPinning } from "@/redux/slices/clientBooking/clientBookingSlice";
import { selectActiveBookingField, selectActiveFieldLocation, selectIsPinning } from "@/redux/slices/clientBooking/selectors";
import { clsx, getCountryName } from "@/utils";

const fieldLabel = (kind: string | undefined): string => (kind === "destination" ? "Dropoff" : kind === "stop" ? "Extra Stop" : "Pickup");

const TITLE = "Pin a location";
const DESCRIPTION = "Search an address or drag the map to pinpoint a location, then confirm below.";

/** "Australia", "Australia or New Zealand", "Australia, New Zealand or Fiji". */
const countryList = (codes: string[]): string => {
  const names = codes.map((code) => getCountryName(code.toUpperCase()) || code.toUpperCase());
  return names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
};

interface PinLocationModalProps {
  /**
   * The countries a pinned or searched location may be in (lower-case ISO codes) — the same rule as the
   * sheet's address search, see useAddressSearchCountries. A pin dropped outside them is refused with a
   * message rather than written into the booking (DEV-1606). `null` = no restriction: an operator on behalf
   * whose provider has the Worldwide Address Search add-on (DEV-1612).
   */
  allowedCountries: string[] | null;
}

/**
 * "Pin a location" picker — mirrors the Favourites location picker. Two ways to set the location, both
 * writing the same pending POI: (1) type/search in the autocomplete and pick a suggestion, or (2) drag
 * the purple pin (snaps to road + reverse-geocodes on drop). "Set {field} Location" writes it into the
 * active booking field (which fills that field's input). Centered Modal on md+, bottom-sheet below.
 */
export const PinLocationModal = ({ allowedCountries }: PinLocationModalProps) => {
  const dispatch = useAppDispatch();
  const open = useAppSelector(selectIsPinning);
  const activeField = useAppSelector(selectActiveBookingField);
  const activeFieldLocation = useAppSelector(selectActiveFieldLocation);
  const currentLocation = useCurrentLocation();
  const isMdUp = useIsMdUp();

  const [pendingPoi, setPendingPoi] = useState<PointsOfInterest | null>(null);
  // The autocomplete input's text — kept in sync with the pending POI's address, whether it was set by
  // typing-and-selecting or by dragging the pin. Editable so the user can search a new address.
  const [addressText, setAddressText] = useState("");
  // Why the last pin/selection was refused — the location sits outside `allowedCountries`.
  const [outsideMessage, setOutsideMessage] = useState<string | null>(null);

  // Seed the pin + input with the field's existing location each time the modal opens.
  useEffect(() => {
    if (open) {
      setPendingPoi(activeFieldLocation ?? null);
      setAddressText(activeFieldLocation ? poiFieldLabel(activeFieldLocation) : "");
      setOutsideMessage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Typing → picking a suggestion (local POI or resolved Google place), OR dragging the pin, both set
  // the pin + address the same way.
  const handleSelect = (poi: PointsOfInterest) => {
    setAddressText(poiFieldLabel(poi));
    // The pin mimics the search: a location the search would never have offered cannot be pinned either.
    // A pin whose country could not be resolved is allowed through — that is the existing "Requires
    // Validation" path, and isPoiInCountries treats an unknown country as not judgeable.
    if (allowedCountries !== null && !isPoiInCountries(poi, allowedCountries)) {
      setPendingPoi(null);
      const where = getCountryName(poi.country_code) || poi.country || poi.country_code;
      setOutsideMessage(
        allowedCountries.length
          ? `This location is in ${where}. Bookings can only be made in ${countryList(allowedCountries)}.`
          : `This location is in ${where}, and no bookable countries are configured for this operator.`
      );
      return;
    }
    setOutsideMessage(null);
    setPendingPoi(poi);
  };

  // Dragging the pin snaps to the nearest road then reverse-geocodes, keeping the address input in sync.
  const handlePinChange = async (next: LatLng) => {
    const snapped = await snapToNearestRoad(next);
    if (!window.google?.maps?.Geocoder) {
      handleSelect(geocoderResultToPoi({}, snapped));
      return;
    }
    new window.google.maps.Geocoder().geocode({ location: snapped }, (results, status) => {
      handleSelect(geocoderResultToPoi(status === "OK" && results?.[0] ? results[0] : {}, snapped));
    });
  };

  const close = () => dispatch(setIsPinning(false));
  const confirm = () => {
    if (pendingPoi) dispatch(setActiveFieldLocation(pendingPoi));
    close();
  };

  const seed = activeFieldLocation ? { lat: activeFieldLocation.lat, lng: activeFieldLocation.lng } : currentLocation;
  const confirmLabel = `Set ${fieldLabel(activeField?.kind)} Location`;

  const body = (
    <div className="flex flex-col gap-4">
      <LocationAddressAutocomplete
        value={addressText}
        onChange={setAddressText}
        onSelect={handleSelect}
        userLocation={currentLocation}
        // Surface the client's saved favourites here too, matching the home "Book a trip" search.
        includeFavourites
        countries={allowedCountries}
        placeholder="Search an address or drag the pin"
      />
      {outsideMessage && (
        <Typography variant="small" className="text-danger" role="alert">
          {outsideMessage}
        </Typography>
      )}
      <div
        // Panning/pin-dragging the map must not read as vaul's swipe-to-dismiss on the sheet.
        data-vaul-no-drag
        className={clsx("w-full overflow-hidden rounded-lg bg-neutral-surface-gray", isMdUp ? "h-[400px]" : "h-[360px]")}
      >
        <Map
          pinMode
          center={pendingPoi ? { lat: pendingPoi.lat, lng: pendingPoi.lng } : seed}
          zoom={seed || pendingPoi ? 15 : undefined}
          onPinChange={handlePinChange}
        />
      </div>
    </div>
  );

  if (!isMdUp)
    return (
      <FormBottomSheet
        open={open}
        onClose={close}
        title={TITLE}
        description={DESCRIPTION}
        submitLabel={confirmLabel}
        onSubmit={confirm}
        submitDisabled={!pendingPoi}
      >
        {body}
      </FormBottomSheet>
    );

  return (
    <Modal
      open={open}
      onClose={close}
      variant="web-medium"
      title={TITLE}
      description={DESCRIPTION}
      renderButtons={() => (
        <>
          <Button variant="tertiary" size="md" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={confirm} disabled={!pendingPoi}>
            {confirmLabel}
          </Button>
        </>
      )}
    >
      {body}
    </Modal>
  );
};
